/**
 * Cruza o catálogo estático da vitrine com o estado real vindo da API.
 *
 * Função pura de propósito: é a única regra de negócio da home e concentra as
 * decisões que mais importam (o que é "em breve", a que o usuário tem acesso),
 * então precisa ser testável sem React nem rede.
 */

import type { PlatformCourse } from '../../config/courses';
import type { Topic } from '../../services/topicService';
import type { CourseOption } from '../../services/authService';

export interface ResolvedCourse extends PlatformCourse {
  /** 'soon' = sem conteúdo no banco; 'available' = existe e pode ser acessado. */
  status: 'available' | 'soon';
  /** Nome vindo de `topics`. Só usado como fallback do `title`. */
  topicName?: string;
  /** O usuário logado tem matrícula neste curso. */
  enrolled: boolean;
  /**
   * Curso em que a sessão começou. Serve só para destaque visual — desde que o
   * curso passou a ser o header de cada requisição, não existe mais "curso do
   * token" que decida qualquer coisa.
   */
  isCurrent: boolean;
  /** Matrícula existe mas está bloqueada (ex.: assinatura inativa). */
  isBlocked: boolean;
  /** Bloqueada porque o prazo de acesso venceu (e não por suspensão). */
  accessExpired: boolean;
  /** Último dia de acesso, quando o aluno já vê a data. */
  accessEndDate: string | null;
  /** Id da linha de `users` desta matrícula — necessário para o switch-course. */
  enrollmentUserId?: string;
}

export interface MergeCoursesArgs {
  catalog: PlatformCourse[];
  /** Retorno de GET /topics. `undefined` enquanto carrega ou se falhou. */
  topics: Topic[] | undefined;
  /** true quando GET /topics falhou (distingue erro de "ainda carregando"). */
  topicsErrored?: boolean;
  /** Retorno de GET /auth/my-courses. `undefined` para visitante deslogado. */
  myCourses: CourseOption[] | undefined;
  /** Curso em que a sessão começou, quando logado. Só para destaque. */
  currentTopicId?: number;
}

export function mergeCourses({
  catalog,
  topics,
  topicsErrored = false,
  myCourses,
  currentTopicId,
}: MergeCoursesArgs): ResolvedCourse[] {
  // Só dá para afirmar que um curso não existe se a lista de topics chegou
  // inteira. Enquanto carrega — ou se a chamada falhou — o catálogo é tratado
  // de forma otimista: uma vitrine inteira marcada "em breve" por causa de um
  // blip do backend seria pior do que um card que erra ao ser clicado.
  const topicsAreTrustworthy = Array.isArray(topics) && !topicsErrored;
  const topicsById = new Map((topics ?? []).map((t) => [t.id, t]));
  const enrollmentByTopicId = new Map(
    (myCourses ?? []).map((c) => [c.topicId, c]),
  );

  return catalog
    .map((course): ResolvedCourse => {
      const { topicId } = course;

      // Link externo existe por definição: não há topic a conferir.
      const existsInBackend =
        course.externalUrl != null ||
        (topicId != null && (!topicsAreTrustworthy || topicsById.has(topicId)));

      const enrollment =
        topicId != null ? enrollmentByTopicId.get(topicId) : undefined;

      return {
        ...course,
        status: existsInBackend ? 'available' : 'soon',
        topicName: topicId != null ? topicsById.get(topicId)?.name : undefined,
        enrolled: enrollment != null,
        isCurrent: topicId != null && topicId === currentTopicId,
        isBlocked: enrollment?.isBlocked ?? false,
        accessExpired: enrollment?.accessExpired ?? false,
        accessEndDate: enrollment?.accessEndDate ?? null,
        enrollmentUserId: enrollment?.userId,
      };
    })
    .sort((a, b) => a.order - b.order);
}
